import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaImage, FaTooth, FaBuilding, FaSmile } from 'react-icons/fa';
import PageMeta from './PageMeta';
const categories = [
  { name: 'All', icon: <FaImage className="w-5 h-5" />, filter: 'all' },
  { name: 'Dental Work', icon: <FaTooth className="w-5 h-5" />, filter: 'dental' },
  { name: 'Facilities', icon: <FaBuilding className="w-5 h-5" />, filter: 'facilities' },
  { name: 'Before/After', icon: <FaSmile className="w-5 h-5" />, filter: 'before-after' },
];
const galleryItems = [
  {
    id: 1,
    category: 'dental',
    title: 'Cosmetic Dentistry',
    description: 'Beautiful smile transformation',
    image: 'https://images.unsplash.com/photo-1606811951341-756fdd437682?w=400&h=300&fit=crop',
  },
  {
    id: 2,
    category: 'facilities',
    title: 'Modern Clinic',
    description: 'State-of-the-art dental facility',
    image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=400&h=300&fit=crop',
  },
  {
    id: 3,
    category: 'before-after',
    title: 'Orthodontic Results',
    description: 'Before and after braces treatment',
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
  },
  {
    id: 4,
    category: 'dental',
    title: 'Dental Implants',
    description: 'Advanced implant procedures',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=300&fit=crop',
  },
  {
    id: 5,
    category: 'facilities',
    title: 'Patient Lounge',
    description: 'Comfortable waiting area',
    image: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=300&fit=crop',
  },
  {
    id: 6,
    category: 'before-after',
    title: 'Teeth Whitening',
    description: 'Professional whitening results',
    image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=300&fit=crop',
  },
  {
    id: 7,
    category: 'dental',
    title: 'Pediatric Dentistry',
    description: 'Child-friendly dental care',
    image: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=300&fit=crop',
  },
  {
    id: 8,
    category: 'facilities',
    title: 'X-Ray Room',
    description: 'Advanced diagnostic equipment',
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
  },
];
const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredItems = activeCategory === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === activeCategory);
  return (
    <div className="bg-phoenix-beige min-h-screen">
      <PageMeta
        title="Gallery | Phoenix Dental"
        description="View photos of our state-of-the-art clinic and the beautiful smiles we've crafted."
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-phoenix-beige via-phoenix-white to-phoenix-beige py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            className="text-5xl lg:text-6xl font-bold text-phoenix-black mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our <span className="text-phoenix-gold">Gallery</span>
          </motion.h1>
          <motion.p
            className="text-xl text-phoenix-gray max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Explore our dental work, modern facilities, and patient transformations.
            See the quality and care we bring to every smile.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <FaImage className="text-6xl text-phoenix-gold mx-auto" />
          </motion.div>
        </div>
      </section>
      {/* Filter Buttons */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category, index) => (
              <motion.button
                key={category.filter}
                onClick={() => setActiveCategory(category.filter)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${activeCategory === category.filter
                    ? 'bg-phoenix-gold text-phoenix-white shadow-lg'
                    : 'bg-phoenix-white text-phoenix-black hover:bg-phoenix-gold hover:text-phoenix-white'
                  }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.icon}
                {category.name}
              </motion.button>
            ))}
          </div>
          {/* Gallery Grid */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            layout
          >
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                className="bg-phoenix-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                layout
              >
                <div className="aspect-w-4 aspect-h-3 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-phoenix-black mb-2">{item.title}</h3>
                  <p className="text-phoenix-gray text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};
export default Gallery;
